import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useRef, useState } from 'react';
import type ReCAPTCHA from 'react-google-recaptcha';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { Box } from '@/components/Box';
import { Col } from '@/components/Col';
import { Flex } from '@/components/Flex';
import { AccountRegisterCheckbox } from '@/components/FormFields';
import { TitleField } from '@/components/FormFields/TitleField';
import { FormProvider } from '@/components/FormProvider';
import { InputField } from '@/components/InputField';
import { LegacyButton } from '@/components/LegacyButton';
import { PhoneInput } from '@/components/PhoneInput/PhoneInput';
import { ReCaptcha } from '@/components/ReCaptcha';
import { Typography } from '@/components/Typography';
import type { ContentfulEventFragment } from '@/graphql/contentful/api';
import {
  CAMPAIGN_SUBSCRIBE_RESPONSE_CODE,
  useNewsLetter,
} from '@/hooks/useNewsLetter';
import { useUser } from '@/hooks/useUser';
import { CONTENTSQUARE_MASKED } from '@/lib/forms/const';
import { paths } from '@/lib/paths';
import { getOrigin } from '@/lib/util';
import { formMessages } from '@/messages/forms';
import * as NL from '@/messages/newsletter';
import { useRegionProvider } from '@/providers/RegionProvider';
import { getNameFieldOrder, SortedNameFields } from '@/views/SignUpView/helper';

import type { CampaignSubscriptionSchema } from './schema';
import { campaignSubscriptionSchema } from './schema';

import { eventMessages } from '../messages';
import * as S from './CampaignSubscriptionForm.styles';

export const CampaignSubscriptionForm = ({
  description,
  eventId,
}: ContentfulEventFragment) => {
  const { formatMessage } = useIntl();
  const {
    snapshot: { isAuthenticated, isProcessing, user },
  } = useUser();
  const {
    country,
    locale,
    query: { languageCode },
  } = useRegionProvider();

  const { subscribeCampaignNewsLetter } = useNewsLetter();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>();
  const [isVerifyingRecaptcha, setIsVerifyingRecaptcha] = useState(false);
  const reCaptchaRef = useRef<ReCAPTCHA>(null);
  const disabled = loading || isProcessing || !!user;

  const schema = campaignSubscriptionSchema(user, country, formatMessage);
  const defaultValues = {
    ...schema.getDefault(),
  };
  const formCtx = useForm({
    defaultValues: defaultValues,
    resolver: yupResolver(schema),
  });
  const captcha = formCtx.watch('captcha');

  useEffect(() => {
    if (user?.id) {
      formCtx.reset(defaultValues);
    }
  }, [user?.id]);

  const handleSubmit: SubmitHandler<CampaignSubscriptionSchema> = async ({
    email,
    firstName,
    firstNameKanji,
    lastName,
    lastNameKanji,
    phone,
    phonePrefix,
    reCaptchaResponse,
    title,
  }) => {
    if (!eventId) return;
    setErrorMsg(null);

    try {
      setLoading(true);
      const res = await subscribeCampaignNewsLetter({
        form_subscription_type: eventId,
        website_country_code: country.slug,
        language_code: locale.sfCode,
        first_name: firstName,
        last_name: lastName,
        email,
        result_page: `${getOrigin()}/${country.slug}-${
          locale.slug
        }${paths.subscribe.confirmation.asPath()}`,
        salutation: title?.value ?? '',
        recaptcha_response: reCaptchaResponse,
        phone_prefix: phonePrefix.value,
        phone: phone.replace(/\s+/g, ''),
        ...(!!firstNameKanji && { firstNameKanji }),
        ...(!!lastNameKanji && { lastNameKanji }),
      });
      if (res.code === CAMPAIGN_SUBSCRIBE_RESPONSE_CODE.Success) {
        setIsSubscribed(true);
      } else if (
        res.code === CAMPAIGN_SUBSCRIBE_RESPONSE_CODE.AlreadySubscribed
      ) {
        setErrorMsg(formatMessage(eventMessages.alreadySubscribed));
      } else {
        setErrorMsg(res.message);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const isJapan = country.code === 'JP';
  return (
    <Box>
      {!isSubscribed ? (
        <>
          {description && (
            <S.Desc
              dangerouslySetInnerHTML={{
                __html: documentToHtmlString(description.json),
              }}
            ></S.Desc>
          )}
          {errorMsg && (
            <Typography color='error' textAlign='center'>
              {errorMsg}
            </Typography>
          )}
          <FormProvider
            name='campaign-subscription-form'
            {...formCtx}
            onSubmit={(data) => {
              handleSubmit({
                ...data,
                reCaptchaResponse: reCaptchaRef.current?.getValue() ?? '',
              });
            }}
          >
            <S.Row gapDensity='none'>
              <Col md={6}>
                <TitleField
                  colorVariant='grey'
                  countryCode={country.code}
                  isDisabled={disabled}
                />
              </Col>
            </S.Row>
            <S.Row gapDensity='none'>
              <SortedNameFields
                arr={[
                  {
                    component: (
                      <Col key='firstName' md={6}>
                        <InputField
                          className={CONTENTSQUARE_MASKED}
                          colorVariant='grey'
                          data-testid='NewsLetterFormFirstName'
                          disabled={disabled}
                          name='firstName'
                          placeholder={formatMessage(
                            isJapan
                              ? formMessages.firstNameForJP
                              : formMessages.firstName,
                          )}
                          required
                          style={{
                            borderBottom: '1px solid hsla(0,0%,39%,.3)',
                          }}
                        />
                      </Col>
                    ),
                    order: getNameFieldOrder(country, locale, 'first'),
                  },
                  {
                    component: (
                      <Col key='lastName' md={6}>
                        <InputField
                          autoComplete='family-name'
                          className={CONTENTSQUARE_MASKED}
                          colorVariant='grey'
                          data-testid='NewsLetterFormLastName'
                          disabled={disabled}
                          name='lastName'
                          placeholder={formatMessage(
                            isJapan
                              ? formMessages.lastNameForJP
                              : formMessages.lastName,
                          )}
                          required
                          style={{
                            borderBottom: '1px solid hsla(0,0%,39%,.3)',
                          }}
                        />
                      </Col>
                    ),
                    order: getNameFieldOrder(country, locale, 'last'),
                  },
                  ...(isJapan
                    ? [
                        {
                          component: (
                            <Col key='firstNameKanji' md={6}>
                              <InputField
                                autoComplete='given-name'
                                className={CONTENTSQUARE_MASKED}
                                colorVariant='grey'
                                data-test-id='NewsLetterFormFirstNameKanji'
                                name='firstNameKanji'
                                placeholder={formatMessage(
                                  formMessages.firstNameKanji,
                                )}
                                required
                                style={{
                                  borderBottom: '1px solid hsla(0,0%,39%,.3)',
                                }}
                              />
                            </Col>
                          ),
                          order: getNameFieldOrder(
                            country,
                            locale,
                            'firstKanji',
                          ),
                        },
                        {
                          component: (
                            <Col key='lastNameKanji' md={6}>
                              <InputField
                                autoComplete='family-name'
                                className={CONTENTSQUARE_MASKED}
                                colorVariant='grey'
                                data-test-id='NewsLetterFormLastNameKanji'
                                name='lastNameKanji'
                                placeholder={formatMessage(
                                  formMessages.lastNameKanji,
                                )}
                                required
                                style={{
                                  borderBottom: '1px solid hsla(0,0%,39%,.3)',
                                }}
                              />
                            </Col>
                          ),
                          order: getNameFieldOrder(
                            country,
                            locale,
                            'lastKanji',
                          ),
                        },
                      ]
                    : []),
                ]}
              />
            </S.Row>
            <S.Row gapDensity='none'>
              <Col>
                <InputField
                  autoComplete='email'
                  className={CONTENTSQUARE_MASKED}
                  colorVariant='grey'
                  data-testid='NewsLetterFormEmail'
                  disabled={disabled}
                  id='inputEmail'
                  name='email'
                  placeholder={formatMessage(formMessages.email)}
                  required
                  style={{ borderBottom: '1px solid hsla(0,0%,39%,.3)' }}
                  type='email'
                />
              </Col>
            </S.Row>
            <S.Row gapDensity='none'>
              <Col>
                <PhoneInput fullWidth isProcessing={disabled} />
              </Col>
            </S.Row>
            <S.Row gapDensity='none'>
              <Col data-testid='NewsLetterFormAccountRegisterCheckbox'>
                <AccountRegisterCheckbox
                  action={formatMessage(NL.defined.newsLetterButton)}
                  disabled={loading}
                />
              </Col>
              <Col>
                <ReCaptcha
                  ref={reCaptchaRef}
                  languageCode={languageCode}
                  name='captcha'
                  onChange={setIsVerifyingRecaptcha}
                  validation={false}
                />
              </Col>
              <Col>
                <Flex justifyContent='center'>
                  <LegacyButton
                    data-testid='NewsLetterFormButtonSubmit'
                    disabled={!captcha || isVerifyingRecaptcha || loading}
                    isProcessing={isVerifyingRecaptcha || loading}
                    label={formatMessage(NL.defined.newsLetterButton)}
                    type='submit'
                  />
                </Flex>
              </Col>
            </S.Row>
          </FormProvider>
        </>
      ) : (
        <Typography textAlign='center'>
          {isAuthenticated
            ? NL.NewsLetterSubSuccessDescUser
            : NL.NewsLetterSubSuccessDescGuest}
        </Typography>
      )}
    </Box>
  );
};
